import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { devWarn } from "../../lib/dev-log";
import { langName } from "../../lib/languages";
import CampaignStatusPill from "./CampaignStatusPill";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import {
  Share2, ExternalLink, MoreHorizontal, Play, Pause, Archive, Trash2,
  Eye, Users, CheckCircle2, Sparkles,
} from "lucide-react";

const NUM = (n) => (n == null ? 0 : n);

/**
 * CampaignCard — the atomic unit of the Growth Campaign Centre.
 *
 * Design priority (per Amendment): SHARING is the primary action on every
 * campaign. Everything else (edit, pause, archive) is one click deeper via
 * the overflow menu. "Preview as business leader" is always visible.
 */
export default function CampaignCard({ campaign, onShare, onLinkedIn, onStatusChange, onDelete }) {
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/consultants/me/share-links/${campaign.id}/stats`);
      setStats(data);
    } catch (err) {
      // Stats are ancillary — surface for DevTools but never toast on a card render.
      devWarn(`[CampaignCard] stats fetch failed for ${campaign.id}`, err);
    }
  }, [campaign.id]);

  useEffect(() => { load(); }, [load]);

  const canPause = campaign.status === "active";
  const canResume = ["paused", "draft"].includes(campaign.status);
  const canArchive = campaign.status !== "archived";

  const setStatus = async (next) => {
    try {
      await api.put(`/consultants/me/share-links/${campaign.id}`, { status: next });
      toast.success(`Campaign ${next}`);
      onStatusChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't change status");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${campaign.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/consultants/me/share-links/${campaign.id}`);
      toast.success("Campaign deleted");
      onDelete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete");
    }
  };

  const preview = () => window.open(campaign.url, "_blank", "noopener");

  return (
    <div className="rounded-xl border border-border/60 bg-card/60 hover:border-accent/40 transition-colors flex flex-col" data-testid={`campaign-card-${campaign.id}`}>
      <div className="p-5 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <CampaignStatusPill status={campaign.status} testId={`campaign-status-${campaign.id}`} />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-sans-ui">
                {langName(campaign.default_language)}
              </span>
            </div>
            <h3 className="font-sans-ui font-semibold text-base leading-snug truncate" data-testid={`campaign-name-${campaign.id}`}>
              {campaign.name}
            </h3>
            <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-1">
              <span>{campaign.country}</span>
              <span aria-hidden>·</span>
              <span>{campaign.industry}</span>
              <span aria-hidden>·</span>
              <span>{campaign.function}</span>
              {campaign.target_executive && (<><span aria-hidden>·</span><span className="text-foreground/80">{campaign.target_executive}</span></>)}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1.5 rounded-md hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition" data-testid={`campaign-menu-${campaign.id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              {canPause && (
                <DropdownMenuItem onClick={() => setStatus("paused")} data-testid={`campaign-pause-${campaign.id}`}>
                  <Pause className="w-4 h-4 mr-2" /> Pause campaign
                </DropdownMenuItem>
              )}
              {canResume && (
                <DropdownMenuItem onClick={() => setStatus("active")} data-testid={`campaign-activate-${campaign.id}`}>
                  <Play className="w-4 h-4 mr-2" /> Set active
                </DropdownMenuItem>
              )}
              {canArchive && (
                <DropdownMenuItem onClick={() => setStatus("archived")} data-testid={`campaign-archive-${campaign.id}`}>
                  <Archive className="w-4 h-4 mr-2" /> Archive
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={remove} className="text-red-400 focus:text-red-300" data-testid={`campaign-delete-${campaign.id}`}>
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Momentum strip — the three metrics that matter for a Growth Campaign */}
        <div className="mt-5 grid grid-cols-3 gap-2 rounded-lg border border-border/40 bg-secondary/30 p-3" data-testid={`campaign-stats-${campaign.id}`}>
          <Stat icon={Eye}          label="Views"       value={NUM(stats?.total_opens)} />
          <Stat icon={CheckCircle2} label="Completions" value={NUM(stats?.total_completions)} />
          <Stat icon={Users}        label="Leads"       value={NUM(stats?.total_leads)} tone="accent" />
        </div>
      </div>

      {/* Primary + secondary actions — Share is ALWAYS the primary CTA */}
      <div className="border-t border-border/60 px-5 py-3 flex items-center gap-2 flex-wrap">
        <Button
          onClick={() => onShare?.(campaign)}
          className="flex-1 min-w-[8rem]"
          data-testid={`campaign-share-${campaign.id}`}
        >
          <Share2 className="w-4 h-4 mr-2" /> Share campaign
        </Button>
        <Button variant="outline" size="icon" onClick={preview} title="Preview as business leader" data-testid={`campaign-preview-${campaign.id}`}>
          <ExternalLink className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="icon" onClick={() => onLinkedIn?.(campaign)} title="AI LinkedIn assets" data-testid={`campaign-ai-linkedin-${campaign.id}`}>
          <Sparkles className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="flex flex-col items-start">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-sans-ui">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-1 font-display text-xl leading-none ${tone === "accent" ? "text-accent" : "text-foreground"}`}>
        {value}
      </div>
    </div>
  );
}
